import { SortedPoiItemModel } from "@/models/sortedPoiItem.model";
import { excludedAmenities } from "@/store/data";
import { type ClassValue, clsx } from "clsx";
import fuzzysort from "fuzzysort";
import Feature from "proximiio-js-library/lib/models/feature";
import { FloorModel } from "proximiio-js-library/lib/models/floor";
import removeAccents from "remove-accents";
import { twMerge } from "tailwind-merge";
import { centroid } from "@turf/centroid";
import { PlaceModel } from "proximiio-js-library/lib/models/place";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getFloorName = ({
  floor,
  language,
}: {
  floor: FloorModel;
  language: string;
}) => {
  if (floor) {
    if (floor.metadata && (floor.metadata["title_" + language] as string)) {
      return floor.metadata["title_" + language] as string;
    }
    if (floor.name.length === 1 && Number(parseInt(floor.name))) {
      return `L${floor.name}`;
    }
    return floor.name;
  }
};

const getPlaceName = ({
  place,
  language,
}: {
  place: PlaceModel;
  language: string;
}) => {
  if (place) {
    if (place && place.metadata && place.metadata[`title_${language}`]) {
      return place.metadata[`title_${language}`];
    }
    return place?.name;
  }
  return;
};

export const findFeatureByBayId = (
  number: number,
  features: Feature[],
): Feature | null => {
  const featuresWithBays = features.filter(
    (feature) =>
      feature.properties?.metadata?.bays && feature.properties.type === "poi",
  );
  for (const feature of featuresWithBays) {
    const bays: string = feature.properties?.metadata?.bays;

    const ranges: string[] = bays.split(",").map((str) => str.trim());

    for (const range of ranges) {
      if (range.includes("-")) {
        const [start, end]: number[] = range.split("-").map(Number);
        if (number >= start && number <= end) {
          return feature;
        }
      } else {
        const singleNumber: number = Number(range);
        if (number === singleNumber) {
          return feature;
        }
      }
    }
  }
  return null; // Return null if no matching object is found
};

export const getSortedPOIs = ({
  features,
  floors,
  // currentPlace,
  currentLang,
  // amenities,
  places,
}: {
  features: Feature[];
  floors: FloorModel[];
  // currentPlace: PlaceModel;
  currentLang: string;
  // amenities: AmenityModel[];
  places?: PlaceModel[];
}) => {
  const pois: SortedPoiItemModel[] = features
    .sort((a, b) => (a.properties.title > b.properties.title ? -1 : 1))
    .sort((a, b) => (a.properties.level > b.properties.level ? 1 : -1))
    .map((item) => {
      /*const isInside = isPointWithinRadius(
				{
					lat: item.geometry.coordinates[1],
					lng: item.geometry.coordinates[0],
				},
				{
					lat: currentPlace.location.lat,
					lng: currentPlace.location.lng,
				},
				10000
			);*/
      let place = undefined;
      const floor = floors.find(
        (floor) => floor.id === item.properties.floor_id,
      );

      if (places) {
        place = places.find((place) => place.id === item.properties.place_id);
      }

      // rewrite feature title based on current language
      item.properties.title = item.properties?.title_i18n
        ? item.properties.title_i18n[currentLang]
          ? item.properties.title_i18n[currentLang]
          : item.properties.title_i18n?.en
        : item.properties.title;

      return {
        ...item,
        /*icon: amenities.filter(
					(amenity) => amenity.id === item.properties.amenity
				)[0]
					? amenities.filter(
							(amenity) => amenity.id === item.properties.amenity
						)[0].icon
					: '',
				category: amenities.filter(
					(amenity) => amenity.id === item.properties.amenity
				)[0]
					? amenities.filter(
							(amenity) => amenity.id === item.properties.amenity
						)[0].title
					: '',*/
        search_query: item.properties.title + " " + item.properties.level,
        coordinates: item.geometry.coordinates,
        // isInside,
        score: 0,
        foundInDescription: false,
        floor,
        floorName: floor ? getFloorName({ floor, language: currentLang }) : "",
        place,
        placeName: place ? getPlaceName({ place, language: currentLang }) : "",
      };
    });
  // .filter((item) => item.isInside);
  return pois;
};

export const compareValues = (
  value1: string | string[],
  value2: string | string[],
): boolean => {
  // Check if both values are strings
  if (typeof value1 === "string" && typeof value2 === "string") {
    return value1 === value2;
  }

  // Check if both values are arrays
  if (Array.isArray(value1) && Array.isArray(value2)) {
    // Return true if any element in value1 exists in value2
    return value1.some((item) => value2.includes(item));
  }

  // Check if one value is a string and the other is an array
  if (typeof value1 === "string" && Array.isArray(value2)) {
    return value2.includes(value1);
  }

  if (typeof value2 === "string" && Array.isArray(value1)) {
    return value1.includes(value2);
  }

  // If types are not compatible for comparison, return false
  return false;
};

export const getMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent;

  // iOS detection
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return "iOS";
  }

  // Android detection
  if (/android/i.test(userAgent)) {
    return "Android";
  }

  // Other platforms
  return "Unknown";
};

export const getFilteredItems = ({
  items,
  search,
  language,
}: {
  items: SortedPoiItemModel[];
  search: string;
  language: string;
}) => {
  return items
    .filter((item) => {
      if (excludedAmenities.includes(item.properties.amenity)) return false;
      return true;
    })
    .map((item) => {
      const details =
        item.properties?.description_i18n &&
        item.properties?.description_i18n[language]
          ? item.properties.description_i18n[language]
          : item.properties.description_i18n?.en;
      const title = item.properties.title
        ? removeAccents(item.properties.title)
        : "";
      const term =
        language === "ar"
          ? removeAccents(search.replace(/[\u202B\u202C]/g, "").toLowerCase())
          : removeAccents(search.toLowerCase());
      item.matchedByFuzzy = false; // Flag to indicate fuzzy match
      item.score = 0;
      const titleFuzzy = fuzzysort.single(term, title); // Perform fuzzy search on title
      const keywords = item.properties?.keywords
        ? item.properties.keywords.map((i: string) => i.toLowerCase()).join("_")
        : "";
      const keywords_en = item.properties?.keywords_i18n?.en
        ? item.properties.keywords_i18n.en
            .flat()
            .map((i: string) => i.toLowerCase())
            .join("_")
        : "";
      const keywords_ar = item.properties?.keywords_i18n?.ar
        ? item.properties.keywords_i18n.ar
            .flat()
            .map((i: string) => i.toLowerCase())
            .join("_")
        : "";
      const keywords_ru = item.properties?.keywords_i18n?.ru
        ? item.properties.keywords_i18n.ru
            .flat()
            .map((i: string) => i.toLowerCase())
            .join("_")
        : "";
      const keywords_zh = item.properties?.keywords_i18n?.zh
        ? item.properties.keywords_i18n.zh
            .flat()
            .map((i: string) => i.toLowerCase())
            .join("_")
        : "";
      const spellings_en = item.properties?.spelling?.en
        ? item.properties.spelling.en
            .map((i: string) => i.toLowerCase())
            .join("_")
        : "";
      const spellings_ar = item.properties?.spelling?.ar
        ? item.properties.spelling.ar
            .map((i: string) => i.toLowerCase())
            .join("_")
        : "";
      const spellings_ru = item.properties?.spelling?.ru
        ? item.properties.spelling.ru
            .map((i: string) => i.toLowerCase())
            .join("_")
        : "";
      const spellings_zh = item.properties?.spelling?.zh
        ? item.properties.spelling.zh
            .map((i: string) => i.toLowerCase())
            .join("_")
        : "";
      if (
        title.toLowerCase().includes(term) ||
        (details && details.toLowerCase().includes(term)) ||
        titleFuzzy?.target ||
        keywords.includes(term) ||
        keywords_en.includes(term) ||
        keywords_ar.includes(term) ||
        keywords_ru.includes(term) ||
        keywords_zh.includes(term) ||
        spellings_en.includes(term) ||
        spellings_ar.includes(term) ||
        spellings_ru.includes(term) ||
        spellings_zh.includes(term)
      ) {
        if (title.toLowerCase().startsWith(term)) {
          item.score += 3;

          return item;
        }
        if (title.toLowerCase().indexOf(term) > -1) {
          item.score += 2;

          return item;
        }
        if (
          keywords.includes(term) ||
          keywords_en.includes(term) ||
          keywords_ar.includes(term) ||
          keywords_ru.includes(term) ||
          keywords_zh.includes(term) ||
          spellings_en.includes(term) ||
          spellings_ar.includes(term) ||
          spellings_ru.includes(term) ||
          spellings_zh.includes(term)
        ) {
          item.score += 1;

          item.matchedByFuzzy = true;
          item.fuzzyHighlight = titleFuzzy
            ? (fuzzysort.highlight(titleFuzzy, "*", "*") as string)
            : undefined;

          return item;
        }
        if (titleFuzzy?.target && titleFuzzy?.score > -50000) {
          item.score += titleFuzzy ? titleFuzzy.score : 0;
          item.matchedByFuzzy = true;
          item.fuzzyHighlight = titleFuzzy
            ? (fuzzysort.highlight(titleFuzzy, "*", "*") as string)
            : undefined;

          return item;
        }
        if (details?.toLowerCase().indexOf(term) > -1) {
          item.score += 1;
          item.foundInDescription = true;

          return item;
        }
      }
    })
    .filter((i) => i && i.properties.title?.trim() !== "")
    .sort((a, b) => {
      if (a && b) {
        return (
          b.score - a.score ||
          a.properties.title?.localeCompare(b.properties.title, undefined, {
            sensitivity: "base",
          })
        );
      }
      return 0;
    });
};

export const filterByAmenity = (
  data: SortedPoiItemModel[],
  filterCriteria?: string | string[],
): SortedPoiItemModel[] => {
  if (!filterCriteria) {
    return data;
  }
  // Convert filterCriteria to an array if it's a string
  if (typeof filterCriteria === "string") {
    filterCriteria = [filterCriteria];
  }

  // Filter the data based on the criteria
  return data.filter((item) => {
    // Check if the amenity matches any of the filterCriteria
    const amenityMatches = filterCriteria?.includes(item.properties.amenity);

    // Check if any of the included_amenities match any of the filterCriteria
    const includedAmenitiesMatch = item.properties.included_amenities?.some(
      (amenity: string) => filterCriteria?.includes(amenity),
    );

    // Return true if either amenity or included_amenities match
    return amenityMatches || includedAmenitiesMatch;
  });
};

export const getPolygonCentroid = ({
  polygonId,
  features,
}: {
  polygonId: string;
  features: Feature[];
}) => {
  const polygon = features.find((feature) =>
    feature.properties?.id?.includes(polygonId),
  );
  if (polygon) {
    // @ts-expect-error feature is fine
    const center = centroid(polygon);
    if (center) {
      return center.geometry.coordinates;
    }
  }
};

export const pluralize = (word: string): string => {
  if (!word || typeof word !== "string") return "";

  const irregulars: { [key: string]: string } = {
    child: "children",
    man: "men",
    woman: "women",
    tooth: "teeth",
    foot: "feet",
    mouse: "mice",
    person: "people",
    goose: "geese",
  };

  if (irregulars[word.toLowerCase()]) {
    return irregulars[word.toLowerCase()];
  }

  const unchanging: string[] = [
    "sheep",
    "fish",
    "deer",
    "moose",
    "species",
    "aircraft",
    "information",
  ];
  if (unchanging.includes(word.toLowerCase())) {
    return word;
  }

  // Handle multi-word phrases — pluralize the last significant noun only
  if (word.includes(" ")) {
    const words = word.split(" ");
    const lastWord = words[words.length - 1];

    // Only pluralize if the last word is not an unchanging or non-count noun
    if (!unchanging.includes(lastWord.toLowerCase())) {
      words[words.length - 1] = pluralize(lastWord);
    }
    return words.join(" ");
  }

  if (word.endsWith("y") && !/[aeiou]y$/i.test(word)) {
    return word.slice(0, -1) + "ies";
  }

  if (word.endsWith("o") && !/[aeiou]o$/i.test(word)) {
    return word + "es";
  }

  if (word.endsWith("f")) {
    return word.slice(0, -1) + "ves";
  }

  if (word.endsWith("fe")) {
    return word.slice(0, -2) + "ves";
  }

  if (/(s|x|z|sh|ch)$/i.test(word)) {
    return word + "es";
  }

  return word + "s";
};

export const getWayfindingConfig = (accessibleRoute: boolean) => {
  const defaultWayfindingConfig = {
    avoidElevators: true,
    avoidEscalators: false,
    avoidStaircases: false,
    avoidRamps: false,
    avoidNarrowPaths: false,
    avoidRevolvingDoors: false,
    avoidTicketGates: false,
    avoidBarriers: false,
    avoidHills: false,
  };

  return {
    ...defaultWayfindingConfig,
    avoidElevators: accessibleRoute ? false : true,
    avoidRamps: accessibleRoute ? false : true,
    avoidEscalators: accessibleRoute ? true : false,
    avoidStaircases: accessibleRoute ? true : false,
    avoidBarriers: accessibleRoute ? true : false,
    avoidNarrowPaths: accessibleRoute ? true : false,
    avoidRevolvingDoors: accessibleRoute ? true : false,
    avoidTicketGates: accessibleRoute ? true : false,
  };
};

export const compassHeading = (alpha: number, beta: number, gamma: number) => {
  // Convert degrees to radians
  const alphaRad = alpha * (Math.PI / 180);
  const betaRad = beta * (Math.PI / 180);
  const gammaRad = gamma * (Math.PI / 180);

  // Calculate equation components
  const cA = Math.cos(alphaRad);
  const sA = Math.sin(alphaRad);
  // const cB = Math.cos(betaRad);
  const sB = Math.sin(betaRad);
  const cG = Math.cos(gammaRad);
  const sG = Math.sin(gammaRad);

  // Calculate A, B, C rotation components
  const rA = -cA * sG - sA * sB * cG;
  const rB = -sA * sG + cA * sB * cG;
  // const rC = -cB * cG;

  // Calculate compass heading
  let compassHeading = Math.atan(rA / rB);

  // Convert from half unit circle to whole unit circle
  if (rB < 0) {
    compassHeading += Math.PI;
  } else if (rA < 0) {
    compassHeading += 2 * Math.PI;
  }

  // Convert radians to degrees
  compassHeading *= 180 / Math.PI;

  return compassHeading;
};
